/**
 * The provenance line that must accompany every number on this dashboard.
 *
 * Three separate things can be true of a figure here and none of them are
 * visible in the figure itself:
 *
 * 1. It is *old*. The Cloudflare query is deliberately backdated past the
 *    finalization lag, so even a cache refreshed one second ago describes a
 *    window that ended ~10 minutes back.
 * 2. It is an *estimate*. Cloudflare adaptive-sampling scales counts up from a
 *    sample. `1,250` may mean "we saw 125 and multiplied by 10".
 * 3. It is *stale*. On an upstream failure the API serves the last good
 *    snapshot rather than a 500, so the screen keeps working and stops being
 *    current at the same time.
 *
 * The "behind" figure is recomputed here rather than read straight off
 * `meta.age_seconds`. That field is calculated server-side at response time and
 * then frozen into the JSON, so a tab left open for an hour would go on
 * displaying "10 min behind" for hour-old numbers — exactly the false-freshness
 * claim this badge exists to prevent.
 *
 * The sampling badge is always on screen rather than hidden behind a hover:
 * every count on a sampled window is an extrapolation, and rendering an
 * extrapolation as a bare exact-looking integer is the misleading part.
 */
import { useEffect, useState } from 'react'

import { formatPacific, useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import type { Meta } from '../api/types'

const PILL = 'rounded px-1.5 py-0.5 font-medium'

/** How often the "behind" figure is redrawn. Fine enough that the number is
 *  never more than half a minute out, coarse enough to be free. */
const TICK_MS = 30_000

export function FreshnessBadge({ meta, className = '' }: { meta: Meta; className?: string }) {
  const { lang, t } = useI18n()
  const asOf = formatPacific(meta.as_of, lang)
  const age = useLiveAge(meta)

  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs text-muted ${className}`}>
      <span>
        {asOf === null
          ? t('dashboard.freshness.noTimestamp')
          : t('dashboard.freshness.asOf', { time: asOf })}
      </span>

      <span aria-hidden="true">·</span>
      <span title={t('dashboard.freshness.lagTitle')}>
        {t('dashboard.freshness.behind', { age: formatAge(age, t) })}
      </span>

      {meta.sampled && (
        <span
          className={`${PILL} bg-degraded/15 text-degraded`}
          title={sampleTitle(meta.avg_sample_interval, t)}
        >
          {t('dashboard.freshness.estimated')}
        </span>
      )}

      {meta.degraded && (
        // role=status, not a silent pill: the screen just stopped updating and
        // nothing else on it looks any different.
        <span role="status" className={`${PILL} bg-down/15 text-down`}>
          {t('dashboard.freshness.stale')}
        </span>
      )}
    </div>
  )
}

/**
 * How far behind real time these numbers are, right now.
 *
 * Measured from `as_of` (the end of the data window) against a clock that keeps
 * ticking, and floored at the server's own `age_seconds`: the backend knows
 * about Cloudflare's finalization lag and a browser clock skewed behind the
 * server must never be allowed to report the data as fresher than it is.
 * A timestamp we cannot parse leaves the server's figure as the only answer.
 */
function useLiveAge(meta: Meta): number {
  // The clock is an external system, so it is read in an effect and held in
  // state rather than called during render.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  const asOfMs = new Date(meta.as_of).getTime()
  if (Number.isNaN(asOfMs)) return meta.age_seconds
  return Math.max((now - asOfMs) / 1000, meta.age_seconds)
}

/** A bare quantity ("11 min", "11 分钟"), which `dashboard.freshness.behind`
 *  then wraps — a self-contained unit, not a sentence built from fragments. */
function formatAge(seconds: number, t: TFunction): string {
  if (!Number.isFinite(seconds) || seconds < 0) return t('dashboard.freshness.ageUnknown')
  if (seconds < 90) return t('dashboard.freshness.ageSeconds', { n: Math.round(seconds) })
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return t('dashboard.freshness.ageMinutes', { n: minutes })
  return t('dashboard.freshness.ageHours', { n: (minutes / 60).toFixed(1) })
}

function sampleTitle(interval: number | null, t: TFunction): string {
  const rate =
    interval && interval > 1
      ? t('dashboard.freshness.sampleRate', { n: Math.round(interval) })
      : t('dashboard.freshness.sampleRateUnknown')
  return t('dashboard.freshness.sampleTitle', { rate })
}
